import React from 'react';
import { C } from '../../theme.js';
import { Alert, Eyebrow, FieldLabel, TextInput } from '../../components/Primitives.jsx';
import { Segmented } from '../StepInspector.jsx';
import { MultiColsDropdown } from './MultiColsDropdown.jsx';
import { hasUpstreamSplit } from '../../data.js';
import { DecimalPlacesField } from '../shared/DecimalPlacesField.jsx';
import { OutputColumnNamesField } from '../shared/OutputColumnNamesField.jsx';

export function FixedClippingInspector({ step, onChange, pipeline }) {
  const c = step.config || {};
  const sel = Array.isArray(c.columns) ? c.columns : (c.column ? [c.column] : []);
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  const splitReady = pipeline ? hasUpstreamSplit(pipeline.steps, step.id) : true;

  return (
    <div>
      <Eyebrow>Fixed Clipping</Eyebrow>
      <div style={{ color: C.fg4, fontSize: '.78rem', margin: '4px 0 10px', lineHeight: 1.5 }}>
        Cap or drop numerical outliers using absolute min and max bounds.
      </div>

      {!splitReady && (
        <Alert kind="warn" style={{ marginBottom: 10 }}>
          Warning: No Train / Test Split step detected upstream. If your end goal is machine learning modeling, please add a Train / Test Split block before performing output handling to avoid data leakage.
        </Alert>
      )}

      {/* Single-column only: the lower/upper bounds below are absolute
          values tied to one column's own scale (e.g. bounds of 30/17 make
          sense for Age but not Salary) — there's no way for one shared
          bound pair to correctly apply across columns on different scales.
          To clip multiple columns with different bounds, add this block
          again per column. */}
      <MultiColsDropdown selected={sel} onChange={(v) => u({ columns: v, column: v[0] || null })} numeric single />

      <div style={{ marginTop: 10 }}>
        <FieldLabel hint="Cap values outside bounds or drop outlier rows">Action on Outliers</FieldLabel>
        <Segmented
          value={c.action || 'clip'}
          onChange={(v) => u({ action: v })}
          options={[
            { value: 'clip', label: 'Clip (Cap values)' },
            { value: 'drop', label: 'Drop rows' }
          ]}
        />
      </div>

      <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <FieldLabel hint="Minimum allowed numeric value (leave blank for none)">Lower Bound (Min)</FieldLabel>
          <TextInput
            type="number"
            value={c.lower_bound ?? ''}
            onChange={(v) => u({ lower_bound: v })}
            placeholder="-∞"
          />
        </div>
        <div>
          <FieldLabel hint="Maximum allowed numeric value (leave blank for none)">Upper Bound (Max)</FieldLabel>
          <TextInput
            type="number"
            value={c.upper_bound ?? ''}
            onChange={(v) => u({ upper_bound: v })}
            placeholder="+∞"
          />
        </div>
      </div>

      {c.action !== 'drop' && (
        <OutputColumnNamesField
          columns={sel}
          value={c.output_columns}
          onChange={(v) => u({ output_columns: v })}
          legacyValue={c.output_column}
          autoName={(col) => `${col}_clipped`}
        />
      )}
      <DecimalPlacesField value={c.output_decimals} onChange={(v) => u({ output_decimals: v })} />
    </div>
  );
}
